/// <amd-module name="Feed/Legacy/Protocols/Npm/NpmPackageProtocol" />
import * as React from "react";

import * as Utils_String from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";

import { FwLinks, PackageCommandIds } from "Feed/Common/Constants/Constants";
import { FeedRole, FeedView, Package, PackageMetrics, PackageVersion, PackageVersionMetrics } from "Feed/Common/Contracts/Feed";
import { UpstreamingBehavior } from "Feed/Common/Protocols/Shared/Contracts/PackagingShared";
import * as PackageResources from "Feed/Common/Resources";
import { IGeneralDialogProps } from "Feed/Common/Types/IGeneralDialogProps";
import { BowtieIconProps } from "Feed/Common/Utils/Icons";
import * as Actions from "Feed/Legacy/Actions/Actions";
import { ProtocolCommands } from "Feed/Legacy/Common/ProtocolCommands";
import { isV2Feed } from "Feed/Legacy/Helpers/FeedCapabilityHelper";
import * as PermissionHelper from "Feed/Legacy/Helpers/PermissionHelper";
import { BatchGenerator, IBatchIterator, TransformingBatchGenerator } from "Feed/Legacy/Protocols/Common/BatchHandling";
import { DeleteCommandHelper } from "Feed/Legacy/Protocols/Common/DeleteCommandHelper";
import { IPackageDetailsProps } from "Feed/Legacy/Protocols/Common/IPackageDetailsProps";
import { IPackageProtocol, IPackageProtocolClient, isPackage } from "Feed/Legacy/Protocols/Common/IPackageProtocol";
import { ISupportedFeatures } from "Feed/Legacy/Protocols/Common/ISupportedFeatures";
import {
    DownloadPackageCommand,
    PromotePackageCommand,
    PromotePackagesCommand
} from "Feed/Legacy/Protocols/Common/PackageCommands";
import { MinimalPackageDetails } from "Feed/Legacy/Protocols/Common/WebApi/PackagingShared.Contracts";
import { DeprecatePackageCommand, DeprecatePackagesCommand } from "Feed/Legacy/Protocols/Npm/Commands/NpmDeprecateCommand";
import { NpmKey } from "Feed/Legacy/Protocols/Npm/Constants/NpmConstants";
import { NpmClientTool } from "Feed/Legacy/Protocols/Npm/NpmClientTool";
import { INpmDataService } from "Feed/Legacy/Protocols/Npm/NpmDataService";
import { NpmOverviewAttributesPanel } from "Feed/Legacy/Protocols/Npm/NpmOverviewAttributesPanel";
import { NpmOverviewContentPanel } from "Feed/Legacy/Protocols/Npm/NpmOverviewContentPanel";
import { INpmRestApiUrlGeneratorService } from "Feed/Legacy/Protocols/Npm/NpmRestApiUrlGeneratorService";
import { IPackageCommand } from "Feed/Legacy/Types/IPackageCommand";
import { Feed } from "Feed/Legacy/WebApi/VSS.CustomFeed.Contracts";

export class NpmPackageProtocol implements IPackageProtocol {
    private static readonly _icon = new BowtieIconProps("brand-npm");

    public readonly name = NpmKey;
    public readonly key = "Npm";
    get vssIconProps() {
        return NpmPackageProtocol._icon;
    }
    public supportedCommandsMask: ProtocolCommands =
        // tslint:disable-next-line:no-bitwise
        ProtocolCommands.Promote |
        ProtocolCommands.Delete |
        ProtocolCommands.Download |
        ProtocolCommands.Deprecate |
        ProtocolCommands.Undeprecate;

    public readonly clients: IPackageProtocolClient[] = [];
    private pageContext: IVssPageContext;

    constructor(pageContext: IVssPageContext) {
        this.pageContext = pageContext;
        this.clients.push(new NpmClientTool(this.pageContext));
    }

    get NpmDataService(): INpmDataService {
        return this.pageContext.getService<INpmDataService>("INpmDataService");
    }

    get NpmRestApiUrlGeneratorService(): INpmRestApiUrlGeneratorService {
        return this.pageContext.getService<INpmRestApiUrlGeneratorService>("INpmRestApiUrlGeneratorService");
    }

    public getPackageMessage(packageSummary: Package, packageVersion: PackageVersion): string {
        if (packageVersion) {
            if (packageVersion.isDeleted) {
                return Utils_String.format(
                    PackageResources.PackageUnpublishedMessage,
                    packageVersion.version,
                    packageSummary.name
                );
            } else if (
                packageVersion.protocolMetadata &&
                packageVersion.protocolMetadata.data.deprecated &&
                packageVersion.protocolMetadata.data.deprecated.toLowerCase().trim() !== "false" &&
                packageVersion.protocolMetadata.data.deprecated.trim().length > 0
            ) {
                return Utils_String.format(
                    PackageResources.PackageDeprecatedMessage,
                    packageVersion.version,
                    packageSummary.name,
                    packageVersion.protocolMetadata.data.deprecated
                );
            }
        }
        return null;
    }

    public getOverviewAttributesPanel(
        feed: Feed,
        packageSummary: Package,
        packageVersion: PackageVersion,
        packageVersionMetrics: PackageVersionMetrics,
        packageMetrics: PackageMetrics,
        isSmartDependenciesEnabled: boolean,
        isProvenanceEnabled: boolean,
        isMetricsEnabled: boolean,
        isMetricsLoading: boolean
    ): React.ReactNode {
        return React.createElement(NpmOverviewAttributesPanel, {
            pageContext: this.pageContext,
            feed,
            packageSummary,
            packageVersion,
            protocol: this,
            isSmartDependenciesEnabled,
            packageVersionMetrics,
            packageMetrics,
            isProvenanceEnabled,
            isMetricsEnabled,
            isMetricsLoading
        } as IPackageDetailsProps);
    }

    public getOverviewContentPanel(
        feed: Feed,
        feedViews: FeedView[],
        packageSummary: Package,
        packageVersion: PackageVersion
    ): React.ReactNode {
        return React.createElement(NpmOverviewContentPanel, {
            pageContext: this.pageContext,
            feed,
            feedViews,
            packageSummary,
            packageVersion,
            protocol: this
        } as IPackageDetailsProps);
    }

    public getMultiSelectPackageCommands(
        feed: Feed,
        selectedPackages?: Package[],
        selectedVersions?: PackageVersion[]
    ): IPackageCommand[] {
        const packageCommands: IPackageCommand[] = [];
        let cachedPackagesIncluded: boolean = false;

        const role: FeedRole = PermissionHelper.getUsersRoleForFromFeed(feed);
        const showDisabledCommands = role === FeedRole.None || role === FeedRole.Reader;

        // Trim out cached packages for V1 feeds.
        if (!isV2Feed(feed)) {
            if (selectedPackages != null) {
                const selectedPackagesWithoutCached: Package[] = selectedPackages.filter(
                    (pkg: Package) => pkg.isCached !== true && pkg.versions[0].isCachedVersion !== true
                );
                if (selectedPackagesWithoutCached.length < selectedPackages.length) {
                    cachedPackagesIncluded = true;
                    selectedPackages = selectedPackagesWithoutCached;
                }
            }
            if (selectedVersions != null) {
                const selectedVersionsWithoutCached: PackageVersion[] = selectedVersions.filter(
                    (version: PackageVersion) => version.isCachedVersion !== true
                );
                if (selectedVersionsWithoutCached.length < selectedVersions.length) {
                    cachedPackagesIncluded = true;
                    selectedVersions = selectedVersionsWithoutCached;
                }
            }
        }

        if (selectedPackages && selectedPackages.length > 0) {
            // Promote
            // Only add Promote button on package list for v1
            packageCommands.push(new PromotePackagesCommand(selectedPackages, showDisabledCommands));
        }

        if (selectedVersions && selectedVersions.length > 0) {
            // Deprecate selected versions
            const dialogProps = this._getDeprecateDialogProps(
                cachedPackagesIncluded,
                Utils_String.format(PackageResources.DeprecateDialog_HeaderText_VersionsList, selectedVersions.length),
                PackageResources.DeprecateDialog_ConfirmText_VersionsList,
                null,
                null,
                selectedVersions
            );
            packageCommands.push(
                new DeprecatePackagesCommand(
                    PackageCommandIds.Deprecate,
                    PackageResources.PackageCommands_Deprecate,
                    dialogProps,
                    showDisabledCommands
                )
            );
        } else if (selectedPackages && selectedPackages.length > 0) {
            // Deprecate latest version
            const dialogPropsLatest = this._getDeprecateDialogProps(
                cachedPackagesIncluded,
                PackageResources.DeprecateDialog_HeaderText_LatestVersion,
                PackageResources.DeprecateDialog_ConfirmText_PackageList,
                selectedPackages
            );
            packageCommands.push(
                new DeprecatePackagesCommand(
                    PackageCommandIds.DeprecateLatest,
                    PackageResources.PackageCommands_DeprecatePackage_Latest,
                    dialogPropsLatest,
                    showDisabledCommands
                )
            );
        }

        if ((selectedVersions && selectedVersions.length > 0) || (selectedPackages && selectedPackages.length > 0)) {
            // Unpublish
            const unpublishCommands = DeleteCommandHelper.addDeleteCommands(
                NpmKey,
                selectedPackages,
                selectedVersions,
                null /*packageName */,
                !PermissionHelper.isAdministratorFromRole(role) /*disabled*/,
                cachedPackagesIncluded
            );
            packageCommands.push(...unpublishCommands);
        }

        return packageCommands;
    }

    // Get a list of available package commands for this protocol based on the user's role
    public getPackageCommands(
        packageSummary: Package,
        packageVersion: PackageVersion,
        feed: Feed,
        viaPackageList: boolean = false
    ): IPackageCommand[] {
        const packageCommands: IPackageCommand[] = [];
        if (packageVersion.isDeleted) {
            return packageCommands;
        }

        // Download
        const getDownloadContentUrl = () =>
            Promise.resolve(
                this.NpmRestApiUrlGeneratorService.getDownloadContentUrl(
                    feed,
                    packageSummary.normalizedName,
                    packageVersion.version
                )
            );

        const downloadPackageCommand = new DownloadPackageCommand(
            DownloadPackageCommand.onDownloadPackage,
            getDownloadContentUrl,
            packageSummary,
            packageVersion,
            feed,
            viaPackageList
        );
        packageCommands.push(downloadPackageCommand);

        const role: FeedRole = PermissionHelper.getUsersRoleForFromFeed(feed);
        const isV1FeedAndCached =
            isV2Feed(feed) === false &&
            (packageSummary.isCached === true || packageSummary.versions[0].isCachedVersion === true);
        const showDisabledCommands = role === FeedRole.Reader || role === FeedRole.None || isV1FeedAndCached;

        // Promote
        packageCommands.push(new PromotePackageCommand(packageSummary, viaPackageList, showDisabledCommands));

        // Deprecate
        packageCommands.push(
            new DeprecatePackageCommand(
                this.pageContext,
                packageSummary,
                packageVersion,
                feed,
                viaPackageList,
                showDisabledCommands
            )
        );

        // Unpublish commands
        const selectedPackages: Package[] = viaPackageList ? [packageSummary] : null;
        const selectedVersions: PackageVersion[] = viaPackageList ? null : [packageVersion];
        const deleteCommands = DeleteCommandHelper.addDeleteCommands(
            NpmKey,
            selectedPackages,
            selectedVersions,
            packageSummary.name,
            isV1FeedAndCached || !PermissionHelper.isAdministratorFromRole(role) /*disabled*/
        );
        packageCommands.push(...deleteCommands);

        return packageCommands;
    }

    public supportedFeatures(feed?: Feed): ISupportedFeatures {
        const features: ISupportedFeatures = {
            upstreams: true,
            projectScopedFeeds: true,
            publicUpstreams: true
        };
        return features;
    }

    // Use supportedFeatures instead.
    // Except for connect to feed dialog that is located in the old Feed/Service/Web location.
    public supportsUpstreams(feed?: Feed): boolean {
        return true;
    }

    public async deleteLatestVersions(
        projectId: string = null /*Project scoped feeds not yet enabled for this protocol */,
        feed: Feed,
        pkgs: Package[]
    ): Promise<void> {
        try {
            const iterator = new TransformingBatchGenerator(pkgs, this.NpmDataService.batchSize, (pkg: Package) => {
                return {
                    id: pkg.name,
                    version: pkg.versions[0].normalizedVersion
                };
            });
            await this.NpmDataService.batchDelete(feed, iterator);
        } catch (error) {
            throw new Error(this.key);
        }
    }

    public async deletePackageVersions(
        projectId: string = null /*Project scoped feeds not yet enabled for this protocol */,
        feed: Feed,
        pkg: Package,
        versions: PackageVersion[]
    ): Promise<void> {
        const iterator = new TransformingBatchGenerator(
            versions,
            this.NpmDataService.batchSize,
            (packageVersion: PackageVersion) => {
                return {
                    id: pkg.name,
                    version: packageVersion.normalizedVersion
                };
            }
        );

        await this.NpmDataService.batchDelete(feed, iterator);
    }

    public async restorePackageVersionsToFeed(
        projectId: string,
        feed: Feed,
        pkg: Package,
        versions: PackageVersion[]
    ): Promise<void> {
        const iterator = new TransformingBatchGenerator(
            versions,
            this.NpmDataService.batchSize,
            (packageVersion: PackageVersion) => {
                return {
                    id: pkg.name,
                    version: packageVersion.normalizedVersion
                };
            }
        );

        await this.NpmDataService.batchRestoreToFeed(feed, iterator);
    }

    public async permanentlyDeletePackageVersions(
        projectId: string,
        feed: Feed,
        pkg: Package,
        versions: PackageVersion[]
    ): Promise<void>;
    public async permanentlyDeletePackageVersions(
        projectId: string,
        feed: Feed,
        packageVersions: MinimalPackageDetails[]
    ): Promise<void>;
    public async permanentlyDeletePackageVersions(
        projectId: string,
        feed: Feed,
        pkgOrDetails: Package | MinimalPackageDetails[],
        versions?: PackageVersion[]
    ) {
        let iterator: IBatchIterator<MinimalPackageDetails[]>;
        if (isPackage(pkgOrDetails)) {
            const pkgName = pkgOrDetails.name;
            iterator = new TransformingBatchGenerator(
                versions,
                this.NpmDataService.batchSize,
                packageVersion => <MinimalPackageDetails>{ id: pkgName, version: packageVersion.normalizedVersion }
            );
        } else {
            iterator = new BatchGenerator(pkgOrDetails, this.NpmDataService.batchSize);
        }

        await this.NpmDataService.batchPermanentlyDelete(feed, iterator);
    }

    public promotePackageVersions(
        projectId: string,
        feed: Feed,
        feedView: FeedView,
        pkg: Package,
        versions: PackageVersion[]
    ): Promise<void>;
    public promotePackageVersions(
        projectId: string,
        feed: Feed,
        feedView: FeedView,
        packageVersions: MinimalPackageDetails[]
    ): Promise<void>;
    public async promotePackageVersions(
        projectId: string,
        feed: Feed,
        feedView: FeedView,
        pkgOrDetails: Package | MinimalPackageDetails[],
        versions?: PackageVersion[]
    ): Promise<void> {
        let iterator: IBatchIterator<MinimalPackageDetails[]>;
        if (isPackage(pkgOrDetails)) {
            const pkgName = pkgOrDetails.name;
            iterator = new TransformingBatchGenerator(
                versions,
                this.NpmDataService.batchSize,
                packageVersion => <MinimalPackageDetails>{ id: pkgName, version: packageVersion.normalizedVersion }
            );
        } else {
            iterator = new BatchGenerator(pkgOrDetails, this.NpmDataService.batchSize);
        }

        await this.NpmDataService.batchPromotePackages(feed, feedView.id, iterator);
    }

    private _getDeprecateDialogProps(
        cachedPackagesIncluded: boolean,
        headerText: string,
        confirmText: string,
        selectedPackages: Package[],
        allVersions?: boolean,
        selectedVersions?: PackageVersion[]
    ): IGeneralDialogProps {
        let defaultValue: string;
        if (selectedPackages) {
            const version = selectedPackages[0].versions[0] as PackageVersion;
            defaultValue =
                selectedPackages.length === 1 && version.protocolMetadata && version.protocolMetadata.data
                    ? version.protocolMetadata.data.deprecated
                    : null;
        } else if (selectedVersions) {
            defaultValue =
                selectedVersions.length === 1 &&
                selectedVersions[0].protocolMetadata &&
                selectedVersions[0].protocolMetadata.data
                    ? selectedVersions[0].protocolMetadata.data.deprecated
                    : null;
        }

        const dialogProps: IGeneralDialogProps = {
            headerText,
            confirmText,
            saveButtonText: PackageResources.PackageCommands_Deprecate,
            confirmTextLearnMoreLink: FwLinks.DeleteDialogLearnMoreNpm,
            textField: true,
            textFieldDefaultValue: defaultValue,
            textFieldLabel: PackageResources.DeprecatePackageDialogMessage,
            textFieldPlaceholder: PackageResources.DeprecatePackageDialogPlaceholderMessage,
            onSavePrimaryButtonText: PackageResources.PackageCommands_Deprecate_Deprecating,
            onSaveCallback: (message: string) =>
                Actions.PackageVersionDeprecated.invoke({
                    message,
                    selectedPackages,
                    selectedVersions
                }),
            onDismissCallback: () => Actions.DialogOpenChanged.invoke(null),
            messageBarMessage: cachedPackagesIncluded
                ? PackageResources.DeprecateDialog_CannotDeprecateV1CachedPackages
                : null
        };

        return dialogProps;
    }

    public getCopyInstallCommand(feed: Feed, pkgName: string, pkgVersion: PackageVersion, separator?: string): string {
        return `npm install ${pkgName}@${pkgVersion.version}`;
    }

    public getUpstreamingBehavior(feed: Feed, pkg: Package): Promise<UpstreamingBehavior> {
        return this.NpmDataService.getUpstreamingBehavior(feed, pkg);
    }

    public setUpstreamingBehavior(feed: Feed, pkg: Package, behavior: UpstreamingBehavior): Promise<void> {
        return this.NpmDataService.setUpstreamingBehavior(feed, pkg, behavior);
    }
}
